/**
 * Spec 004.5 — `createEngineAdapter` factory.
 *
 * PR1o landed the factory dispatch skeleton with stub adapter
 * classes. PR1p replaces the stubs with real `WasmWebGpuEngineAdapter`
 * (in `webgpu.ts`) and `WasmCpuEngineAdapter` (in `cpu.ts`); the
 * factory now also awaits `loadWasmModule()` before constructing
 * either adapter so the underlying wasm exports are ready.
 *
 * Re-exports from this file preserve the public surface — other
 * modules + tests continue to import `createEngineAdapter`,
 * `WasmWebGpuEngineAdapter`, `WasmCpuEngineAdapter` from here.
 */

import { WasmCpuEngineAdapter } from "./cpu";
import { RemoteEngineAdapter } from "./remote";
import { setRemoteConfig } from "./remoteConfig";
import {
  CrossOriginIsolationError,
  WasmUnavailableError,
  type EngineAdapter,
} from "./types";
import { loadWasmModule, type WasmModuleExports } from "./wasmModule";
import { WasmWebGpuEngineAdapter } from "./webgpu";

// Re-export so the existing PR1o consumer interface is unchanged.
export { WasmCpuEngineAdapter } from "./cpu";
export { WasmWebGpuEngineAdapter } from "./webgpu";
export { RemoteEngineAdapter } from "./remote";

/**
 * Create an `EngineAdapter` for the current build-mode + runtime
 * environment per `engine-adapter-interface.md` §Runtime Backend
 * Selection.
 *
 * - `mode="webgpu"`: probes WebGPU; throws `WasmUnavailableError`
 *   if no GPU adapter.
 * - `mode="cpu"`: requires `crossOriginIsolated === true`; throws
 *   `CrossOriginIsolationError` otherwise.
 * - `mode="auto"` (default): probes WebGPU first; falls back to
 *   CPU. Throws `WasmUnavailableError` if neither is available.
 *
 * Loads the wasm module via `loadWasmModule()` exactly once per
 * process; the cache there ensures subsequent factory calls
 * reuse the same `WasmModuleExports` instance.
 */
export async function createEngineAdapter(
  mode: "webgpu" | "cpu" | "auto" = __ENGINE_MODE__,
): Promise<EngineAdapter> {
  if (mode === "webgpu") {
    const ok = await probeWebGpu();
    if (!ok) {
      throw new WasmUnavailableError(
        "WebGPU adapter unavailable (navigator.gpu missing or requestAdapter returned null)",
      );
    }
    const mod = await loadWasmModuleOrThrow();
    return new WasmWebGpuEngineAdapter(mod);
  }

  if (mode === "cpu") {
    if (!crossOriginIsolated) {
      throw new CrossOriginIsolationError();
    }
    const mod = await loadWasmModuleOrThrow();
    return new WasmCpuEngineAdapter(mod);
  }

  // mode === "auto"
  const webgpuOk = await probeWebGpu();
  if (webgpuOk) {
    const mod = await loadWasmModuleOrThrow();
    return new WasmWebGpuEngineAdapter(mod);
  }
  // eslint-disable-next-line no-console -- selection reason per spec
  console.info(
    "[engine] WebGPU probe failed; falling back to CPU-WASM backend",
  );
  if (!crossOriginIsolated) {
    // eslint-disable-next-line no-console
    console.warn(
      "[engine] CPU backend requires cross-origin isolation (COOP/COEP); unavailable",
    );
    throw new WasmUnavailableError(
      "Neither WebGPU nor CPU-WASM backend is available",
    );
  }
  const mod = await loadWasmModuleOrThrow();
  return new WasmCpuEngineAdapter(mod);
}

/**
 * Spec 010 US1 (T035) — Cloud GPU adapter factory.
 *
 * Probes `GET /v1/health` first to verify the server is reachable +
 * the bearer token authorises; on success queries `GET /v1/info`
 * to grab the hardware label; constructs a `RemoteEngineAdapter`
 * with both. Persists URL + token + hardware label via
 * `setRemoteConfig` so subsequent dashboard loads can rehydrate
 * without re-prompting (CloudGpuModal in PR3k).
 *
 * Auth + network failures both surface as `WasmUnavailableError`
 * with a remote-specific message — the dashboard's existing error
 * paths handle this uniformly (it's the error class
 * `createEngineAdapter` already throws on local backend failures).
 *
 * Separate exported function (not folded into the existing
 * `mode: "webgpu"|"cpu"|"auto"` switch) per spec 010 plan + research
 * R11 — the local factory stays untouched.
 */
export async function createRemoteEngineAdapter(
  url: string,
  token: string,
): Promise<RemoteEngineAdapter> {
  const normalisedUrl = url.replace(/\/+$/, "");
  let healthResponse: Response;
  try {
    healthResponse = await fetch(`${normalisedUrl}/v1/health`, {
      method: "GET",
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new WasmUnavailableError(
      `Cloud GPU server unreachable at ${normalisedUrl}: ${message}`,
    );
  }
  if (!healthResponse.ok) {
    throw new WasmUnavailableError(
      `Cloud GPU server health probe returned ${healthResponse.status} ${healthResponse.statusText}`,
    );
  }

  // Health probe doesn't require auth; the info probe does + is the
  // earliest point we know the token is valid.
  let infoResponse: Response;
  try {
    infoResponse = await fetch(`${normalisedUrl}/v1/info`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new WasmUnavailableError(
      `Cloud GPU server info probe failed: ${message}`,
    );
  }
  if (infoResponse.status === 401 || infoResponse.status === 403) {
    throw new WasmUnavailableError(
      "Cloud GPU server rejected the bearer token — generate a new token on the server and update the dashboard",
    );
  }
  if (!infoResponse.ok) {
    throw new WasmUnavailableError(
      `Cloud GPU server info probe returned ${infoResponse.status} ${infoResponse.statusText}`,
    );
  }

  const infoBody = (await infoResponse.json()) as {
    hardware?: { gpu_name?: unknown };
  };
  const gpuName = infoBody.hardware?.gpu_name;
  const hardwareLabel =
    typeof gpuName === "string" && gpuName !== "" ? gpuName : undefined;

  // Persist for next session. Best-effort — failures (private-mode,
  // quota) must not block the adapter construction.
  try {
    setRemoteConfig({
      url: normalisedUrl,
      token,
      ...(hardwareLabel !== undefined ? { hardwareLabel } : {}),
    });
  } catch {
    // Ignore persistence failures.
  }

  return new RemoteEngineAdapter({
    url: normalisedUrl,
    token,
    ...(hardwareLabel !== undefined ? { hardwareLabel } : {}),
  });
}

// ──────────────────────────────────────────────────────────────────
// Internals
// ──────────────────────────────────────────────────────────────────

async function probeWebGpu(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.gpu) {
    return false;
  }
  try {
    const adapter = await navigator.gpu.requestAdapter();
    return adapter !== null;
  } catch {
    return false;
  }
}

/** Wrap `loadWasmModule()` errors in the typed `WasmUnavailableError`
 * envelope so callers see a stable error class regardless of which
 * underlying mechanism failed (network, wasm init, missing pkg, etc).
 */
async function loadWasmModuleOrThrow(): Promise<WasmModuleExports> {
  try {
    return await loadWasmModule();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new WasmUnavailableError(
      `Failed to load slot-engine-wgpu wasm module: ${message}`,
    );
  }
}
